import classNames from "classnames";
import React from "react";

import "./Overlay.scss";

/**
 * Props for the Overlay component.
 *
 * @description The Overlay component is a semi-transparent background overlay
 * used behind modals, alerts, and other floating elements.
 *
 * @property {() => void} [onClick] - Click event handler. Called when the overlay is clicked.
 * @property {boolean} [open=false] - Whether the overlay is visible.
 *   When true, adds the "active" class to display the overlay.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 *
 * @example
 * // Basic overlay
 * <Overlay open={isOpen} />
 *
 * @example
 * // Overlay with click handler
 * <Overlay
 *   open={isModalOpen}
 *   onClick={handleCloseModal}
 * />
 *
 * @example
 * // Overlay with test ID
 * <Overlay
 *   open={showOverlay}
 *   onClick={handleOverlayClick}
 *   testId="modal-overlay"
 * />
 */
interface Props {
  onClick?: () => void;
  open?: boolean;
  testId?: string;
}

/**
 * A semi-transparent background overlay component.
 *
 * @description Overlay renders a full-screen semi-transparent background that appears
 * behind modals, alerts, and other floating elements. It provides visual separation
 * between the overlay content and the main page content.
 *
 * Features:
 * - Semi-transparent background
 * - Configurable visibility via open prop
 * - Click event support for closing modals
 * - CSS class toggling for show/hide animation
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A div element with overlay styling
 *
 * @see Props for all available properties
 */
export const Overlay: React.FC<Props> = (props) => {
  const { onClick, open = false, testId } = props;

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div
      className={classNames("overlay", { active: open })}
      onClick={handleClick}
      data-testid={testId}
    ></div>
  );
};
